<template>
  <div class="ticket-page">
    <section class="ticket-band">
      <div class="ticket-inner">
        <p>环境创建、连接异常、软件模板需求和课程建议，都可以在这里提交。</p>
      </div>
    </section>

    <section class="ticket-workspace">
      <div class="ticket-panel">
        <div class="panel-head">
          <div>
            <h2>提交问题</h2>
            <p>描述越清楚，处理越快。</p>
          </div>
          <el-tag effect="plain" type="success">Support</el-tag>
        </div>

        <el-form label-position="top">
          <el-form-item label="标题">
            <el-input
              v-model="ticket.title"
              maxlength="60"
              show-word-limit
              placeholder="例如：Redis 集群环境创建后无法连接"
            />
          </el-form-item>

          <el-form-item label="详细描述">
            <el-input
              v-model="ticket.description"
              type="textarea"
              :rows="7"
              maxlength="3000"
              show-word-limit
              resize="none"
              placeholder="请描述你要创建的环境、看到的错误、期望结果；如果是模板需求，请写清软件名称、版本和节点数。"
            />
          </el-form-item>

          <el-form-item label="联系方式">
            <el-input v-model="ticket.contact" maxlength="160" placeholder="邮箱、手机号或其他方便联系到你的方式" />
            <p class="field-tip">默认使用注册邮箱，你可以修改为其他联系方式。</p>
          </el-form-item>

          <el-form-item label="附件（可选）">
            <el-upload v-model:file-list="attachments" drag multiple :auto-upload="false" :limit="5" :on-exceed="handleExceed" :on-change="validateAttachment" accept=".png,.jpg,.jpeg,.webp,.gif,.txt,.log,.json,.yaml,.yml,.conf,.out,.gz,.zip">
              <div class="upload-copy"><strong>拖拽文件到这里，或点击选择</strong><span>支持截图、文本日志及 zip/gz 压缩日志；最多 5 个，单个不超过 10 MB</span></div>
            </el-upload>
            <p class="field-tip warning">上传前请移除密码、密钥、Token、手机号等无关敏感信息。</p>
          </el-form-item>
        </el-form>

        <div class="action-row">
          <el-button type="primary" :loading="submitting" @click="submitTicket">
            提交工单
          </el-button>
          <el-button @click="resetTicket">重置</el-button>
        </div>
      </div>

      <div class="ticket-panel side-panel">
        <div class="panel-head">
          <div>
            <h2>最近工单</h2>
            <p>展示当前账户最近提交的 20 个工单。</p>
          </div>
        </div>

        <div class="ticket-list">
          <div v-for="item in recentTickets" :key="item.id" class="ticket-item">
            <div class="ticket-item-head">
              <strong>{{ item.title }}</strong>
              <span :class="['status-pill', item.statusClass]">{{ item.status }}</span>
            </div>
            <p>{{ item.summary }}</p>
            <small>{{ item.ticket_no }} · {{ item.created_at }}<template v-if="item.attachment_count"> · {{ item.attachment_count }} 个附件</template></small>
          </div>
        </div>

        <div class="support-note">
          <strong>处理建议</strong>
          <p>环境类问题建议带上软件名称、版本、节点数量和报错截图描述；模板需求建议写明练习目标。</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import { ElMessage } from "element-plus";

export default {
  name: "SupportTicket",
  data() {
    return {
      submitting: false,
      ticket: this.createEmptyTicket(),
      attachments: [],
      recentTickets: [],
    };
  },
  mounted() { this.initialize(); },
  methods: {
    createEmptyTicket() {
      return {
        title: "",
        description: "",
        contact: "",
      };
    },
    async initialize() {
      try {
        const session = await axios.get("/api/session");
        this.ticket.contact = session.data.email || "";
        await this.loadTickets();
      } catch (error) {
        ElMessage.error("请先登录后提交工单");
      }
    },
    resetTicket() {
      const contact = this.ticket.contact;
      this.ticket = { ...this.createEmptyTicket(), contact };
      this.attachments = [];
    },
    handleExceed() { ElMessage.warning("最多上传 5 个附件"); },
    validateAttachment(file) {
      if (file.size > 10 * 1024 * 1024) {
        this.attachments = this.attachments.filter((item) => item.uid !== file.uid);
        ElMessage.warning(`${file.name} 超过 10 MB`);
      }
    },
    async loadTickets() {
      const response = await axios.get("/api/support/tickets");
      this.recentTickets = (response.data || []).map((item) => ({
        ...item,
        summary: item.description.length > 70 ? `${item.description.slice(0, 70)}…` : item.description,
        status: { OPEN: "待处理", PROCESSING: "处理中", RESOLVED: "已解决", CLOSED: "已关闭" }[item.status] || item.status,
        statusClass: { OPEN: "open", PROCESSING: "processing", RESOLVED: "done", CLOSED: "done" }[item.status] || "open",
      }));
    },
    async submitTicket() {
      if (!this.ticket.title.trim() || !this.ticket.description.trim() || !this.ticket.contact.trim()) {
        ElMessage({
          message: "请填写标题、详细描述和联系方式。",
          type: "warning",
          offset: 240,
        });
        return;
      }

      this.submitting = true;
      try {
        const data = new FormData();
        data.append("title", this.ticket.title.trim());
        data.append("description", this.ticket.description.trim());
        data.append("contact", this.ticket.contact.trim());
        this.attachments.forEach((item) => { if (item.raw) data.append("attachments", item.raw, item.name); });
        const response = await axios.post("/api/support/tickets", data);
        const ticketNo = response.data.ticket_no;
        this.resetTicket();
        await this.loadTickets();
        ElMessage({
          message: `工单 ${ticketNo} 已提交`,
          type: "success",
          offset: 240,
        });
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "工单提交失败，请稍后重试");
      } finally { this.submitting = false; }
    },
  },
};
</script>

<style scoped>
.ticket-page {
  min-height: calc(100vh - 80px);
  background: #f7f9fc;
  color: #253142;
  text-align: left;
}

.ticket-band {
  background: #f4f8fa;
  border-bottom: 1px solid #e3ebef;
}

.ticket-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 24px 14px;
}

h1,
h2,
p {
  margin: 0;
}

h1 {
  font-size: 24px;
  line-height: 1.3;
  font-weight: 700;
}

.ticket-inner p {
  margin-top: 6px;
  color: #667785;
  font-size: 14px;
  line-height: 1.55;
}

.ticket-workspace {
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 24px 36px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
}

.ticket-panel {
  background: #fff;
  border: 1px solid #e4eaf0;
  border-radius: 8px;
  padding: 18px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-head h2 {
  font-size: 18px;
  line-height: 1.3;
}

.panel-head p {
  margin-top: 5px;
  color: #7a8996;
  font-size: 13px;
}

.inline-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field-tip { margin: 6px 0 0; color: #83909d; font-size: 12px; }.field-tip.warning { color: #9b6b24; }.upload-copy { display: grid; gap: 7px; color: #52677b; }.upload-copy span { color: #8795a3; font-size: 12px; }.ticket-panel :deep(.el-upload), .ticket-panel :deep(.el-upload-dragger) { width: 100%; }.ticket-panel :deep(.el-upload-dragger) { padding: 26px 18px; }

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.side-panel {
  align-self: start;
}

.ticket-list {
  display: grid;
  gap: 10px;
}

.ticket-item {
  border: 1px solid #e4eaf0;
  border-radius: 8px;
  padding: 12px;
  background: #fbfcfe;
}

.ticket-item-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.ticket-item strong {
  font-size: 14px;
  line-height: 1.45;
}

.ticket-item p {
  margin-top: 7px;
  color: #667785;
  font-size: 13px;
  line-height: 1.5;
}

.ticket-item small {
  display: block;
  margin-top: 8px;
  font-size:12px;
  color: #95a3ad;
}

.status-pill {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
}

.status-pill.open {
  color: #8a5a00;
  background: #fff6d8;
}

.status-pill.processing {
  color: #1d5f8f;
  background: #e5f3ff;
}

.status-pill.done {
  color: #236b50;
  background: #e6f6ef;
}

.support-note {
  margin-top: 16px;
  border-top: 1px solid #e7edf3;
  padding-top: 14px;
}

.support-note strong {
  font-size: 14px;
}

.support-note p {
  margin-top: 6px;
  color: #667785;
  font-size: 13px;
  line-height: 1.65;
}

@media (max-width: 980px) {
  .ticket-workspace,
  .inline-fields {
    grid-template-columns: 1fr;
  }
}
</style>
